/**
 * PerfectaGEL savings calculator — calculation contract.
 * Mirrors the EU-US Toggle sheet of the customer workbook.
 */

export const REGIONS = {
	EU: {
		cur: '€',
		mass: 'kg',
		big: 't',
		bigDiv: 1000,
		priceHint: 'EU industrial average: €0.12 to €0.25 / kWh',
		co2: 0.233,
		co2Note: 'EU grid average 0.233 kg CO₂ / kWh (Eurostat 2024)',
		co2Disclaimer: 'CO₂ factor: 0.233 kg CO₂ / kWh, EU electricity grid average (Eurostat 2024).',
		volume: { min: 200, max: 10000, step: 100 },
		defaults: {
			FS: { dry: 48, vol: 2000, kw: 20, price: 0.15 },
			ISET: { dry: 36, vol: 2000, kw: 18, price: 0.15 },
		},
		isetCapNote: 'Same cabinet, same time. Additional cycles enabled by faster drying',
		isetModel: 'throughput',
	},
	US: {
		cur: '$',
		mass: 'lb',
		big: 'tons',
		bigDiv: 2000,
		priceHint: 'US industrial: $0.07 to $0.20 / kWh, $0.14 typical',
		co2: 0.81,
		co2Note: 'US grid average 0.81 lb CO₂ / kWh (EIA 2023)',
		co2Disclaimer: 'CO₂ factor: 0.81 lb CO₂ / kWh, US electricity grid average (EIA 2023).',
		volume: { min: 400, max: 22000, step: 200 },
		defaults: {
			FS: { dry: 48, vol: 2200, kw: 20, price: 0.14 },
			ISET: { dry: 40, vol: 2200, kw: 18, price: 0.14 },
		},
		isetCapNote: 'Faster drying frees cabinet time for additional volume on the same schedule',
		isetModel: 'freed',
	},
};

export const ROUTES = {
	FS: {
		dryHint: 'Typical regular potato starch: 36 to 72 h',
		scenLabel: 'Drying time reduction scenario',
		scenarios: {
			Conservative: { label: '−25%', reduction: 0.25 },
			Realistic: { label: '−35%', reduction: 0.35 },
			'Best case': { label: '−50%', reduction: 0.50 },
		},
		claim: '<span class="pg-claim-sm">up to</span><span class="pg-claim-lg">50%</span><span class="pg-claim-md">faster</span><span class="pg-claim-sm">drying time</span>',
		trial: 'Up to 50% drying time reduction is confirmed in Avebe innovation center trials.',
		capNote: 'Cabinet time freed at your current production rate',
		note: 'PerfectaGEL® FS works as a 1% on-top-of addition. No recipe overhaul, no capex. Texture stays firm and clean-label, declared simply as potato starch.',
		disclaimer: 'Disclaimer: Results are calculated from the inputs you provide and from published Avebe performance ranges (PerfectaGEL® FS: up to 50% drying time reduction). Case study figures are based on documented projects but actual outcomes vary by recipe, equipment, ingredient mix and process parameters.',
	},
	ISET: {
		dryHint: 'Typical regular starch route: 36 to 72 h',
		scenLabel: 'Drying time with PerfectaGEL® ISET',
		scenarios: {
			Conservative: { label: '8 h', newDry: 8 },
			Realistic: { label: '6 h', newDry: 6 },
			'Best case': { label: '3 h', newDry: 3 },
		},
		claim: '<span class="pg-claim-sm">as little as</span><span class="pg-claim-lg">3 h</span><span class="pg-claim-sm">drying time</span>',
		trial: '3 h drying is confirmed in Avebe innovation center trials.',
		note: 'PerfectaGEL® ISET enables starchless rigid-mold production with pH stability for vitamin or mineral fortification. Drop-in replacement, no capex required.',
		disclaimer: 'Disclaimer: Results are calculated from the inputs you provide and from published Avebe performance ranges (PerfectaGEL® ISET: drying times of as little as approx. 3 h). Case study figures are based on documented projects but actual outcomes vary by recipe, equipment, ingredient mix and process parameters.',
	},
};

export const num = ( n ) => ( Number.isFinite( n ) ? n : 0 );

export const fmt = ( n, d = 0 ) => num( n ).toLocaleString( 'en-US', {
	minimumFractionDigits: d,
	maximumFractionDigits: d,
} );

/**
 * @param {object} state Calculator input state.
 * @param {string} [scenarioName] Optional scenario override for the yearly range.
 */
export function compute( state, scenarioName ) {
	const R = REGIONS[ state.region ];
	const scenKey = scenarioName || state.scenario;
	const sc = ROUTES[ state.route ].scenarios[ scenKey ];
	const dry = num( state.dry );
	const vol = num( state.vol );
	const kw = num( state.kw );
	const price = num( state.price );
	const cyclesYear = num( state.cycles ) * num( state.weeks );
	const cabinets = Math.max( 1, Math.round( num( state.cabinets ) || 1 ) );
	const yearScale = cyclesYear * cabinets;

	let newDry;
	let hoursFreed;
	let capCycle;
	if ( state.route === 'FS' ) {
		newDry = dry * ( 1 - sc.reduction );
		hoursFreed = dry * sc.reduction;
		capCycle = vol * sc.reduction;
	} else {
		newDry = sc.newDry;
		hoursFreed = Math.max( 0, dry - newDry );
		capCycle = R.isetModel === 'throughput'
			? ( newDry > 0 ? vol * hoursFreed / newDry : 0 )
			: ( dry > 0 ? vol * hoursFreed / dry : 0 );
	}

	const kwhCycle = hoursFreed * kw;
	const costCycle = kwhCycle * price;
	const co2Cycle = kwhCycle * R.co2;

	return {
		R,
		sc,
		scenKey,
		dry,
		newDry,
		hoursFreed,
		cyclesYear,
		cabinets,
		capCycle,
		kwhCycle,
		costCycle,
		co2Cycle,
		throughput: newDry > 0 ? dry / newDry : 0,
		capYear: capCycle * yearScale / R.bigDiv,
		costYear: costCycle * yearScale,
		kwhYear: kwhCycle * yearScale,
		co2Year: co2Cycle * yearScale / R.bigDiv,
	};
}

export const ACCEPTANCE_CASES = [
	[ '1 EU FS', { region: 'EU', route: 'FS', dry: 48, vol: 2000, kw: 20, price: 0.15, scenario: 'Conservative', cycles: 5, weeks: 50, cabinets: 1 } ],
	[ '2 EU ISET', { region: 'EU', route: 'ISET', dry: 36, vol: 2000, kw: 18, price: 0.15, scenario: 'Conservative', cycles: 5, weeks: 50, cabinets: 1 } ],
	[ '3 US FS', { region: 'US', route: 'FS', dry: 48, vol: 2200, kw: 20, price: 0.14, scenario: 'Conservative', cycles: 5, weeks: 50, cabinets: 1 } ],
	[ '4 US ISET', { region: 'US', route: 'ISET', dry: 40, vol: 2200, kw: 18, price: 0.14, scenario: 'Conservative', cycles: 5, weeks: 50, cabinets: 1 } ],
];

export function runAcceptanceTests() {
	return ACCEPTANCE_CASES.map( ( [ name, inp ] ) => {
		const c = compute( inp );
		return {
			test: name,
			cycle: `${ fmt( c.capCycle ) } ${ c.R.mass } · ${ c.R.cur }${ fmt( c.costCycle ) } (${ fmt( c.kwhCycle ) } kWh) · ${ fmt( c.co2Cycle ) } ${ c.R.mass }`
				+ ( inp.route === 'ISET' && c.R.isetModel === 'throughput' ? ` · ${ fmt( c.throughput, 1 ) }×` : '' ),
			year: `${ fmt( c.capYear, 1 ) } ${ c.R.big } · ${ c.R.cur }${ fmt( c.costYear ) } · ${ fmt( c.co2Year, 1 ) } ${ c.R.big }`,
		};
	} );
}

export const ACCEPTANCE_EXPECTED = {
	'1 EU FS': {
		cycle: '500 kg · €36 (240 kWh) · 56 kg',
		year: '125.0 t · €9,000 · 14.0 t',
	},
	'2 EU ISET': {
		cycle: '7,000 kg · €76 (504 kWh) · 117 kg · 4.5×',
		year: '1,750.0 t · €18,900 · 29.4 t',
	},
	'3 US FS': {
		cycle: '550 lb · $34 (240 kWh) · 194 lb',
		year: '68.8 tons · $8,400 · 24.3 tons',
	},
	'4 US ISET': {
		cycle: '1,760 lb · $81 (576 kWh) · 467 lb',
		year: '220.0 tons · $20,160 · 58.3 tons',
	},
};

